import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { CheckOtpRequestViewModel } from '../models/CheckOtpRequestViewModel';
import type { EmptyResponseViewModel } from '../models/EmptyResponseViewModel';
import type { GetActivityViewModel } from '../models/GetActivityViewModel';
import type { GetOtpViewModel } from '../models/GetOtpViewModel';
import type { PasswordResetViewModel } from '../models/PasswordResetViewModel';
import type { PasswordUpdateViewModel } from '../models/PasswordUpdateViewModel';
import type { RefreshTokenRequestViewModel } from '../models/RefreshTokenRequestViewModel';
import type { RegisterV2ResponseViewModel } from '../models/RegisterV2ResponseViewModel';
import type { RegisterV2ViewModel } from '../models/RegisterV2ViewModel';
import type { SendOtpRequestViewModel } from '../models/SendOtpRequestViewModel';
import type { SendOtpViewModel } from '../models/SendOtpViewModel';
import type { TokenRequestViewModel } from '../models/TokenRequestViewModel';
import type { TokenV2ViewModel } from '../models/TokenV2ViewModel';
import type { TokenViewModel } from '../models/TokenViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class AuthenticationService {
  /**
   * Token
   * Gets access token by username and password
   * @returns TokenV2ViewModel Success
   * @throws ApiError
   */
  public static async token(
    {
      body,
    }: {
      body?: TokenRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TokenV2ViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<TokenV2ViewModel>({
      method: 'POST',
      url: `/v2/auth/token`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Token Refresh
   * Gets access token by refresh token
   * @returns TokenViewModel Success
   * @throws ApiError
   */
  public static async tokenRefresh(
    {
      body,
    }: {
      body?: RefreshTokenRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TokenViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<TokenViewModel>({
      method: 'POST',
      url: `/v2/auth/token/refresh`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Register with Loyalty
   * Registers an user
   * @returns RegisterV2ResponseViewModel Success
   * @throws ApiError
   */
  public static async register(
    {
      body,
    }: {
      body?: RegisterV2ViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<RegisterV2ResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<RegisterV2ResponseViewModel>({
      method: 'POST',
      url: `/v2/auth/register`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Password Reset
   * I forgot my password
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async passwordReset(
    {
      body,
    }: {
      body?: PasswordResetViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/auth/password/reset`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Password Update
   * Updates a password
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async passwordUpdate(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      body?: PasswordUpdateViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'PATCH',
      url: `/v2/auth/password/update`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Otp Validation
   * Gets last valid otp date and status
   * @returns GetOtpViewModel Success
   * @throws ApiError
   */
  public static async checkOtpDate(
    {
      type,
      countryCode,
      identifier,
    }: {
      type: any;
      countryCode?: string;
      identifier?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetOtpViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GetOtpViewModel>({
      method: 'GET',
      url: `/v2/auth/otp`,
      query: {
        ...(countryCode ? { countryCode: countryCode } : {}),
        ...(identifier ? { identifier: identifier } : {}),
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Send Otp
   * Sends otp code according to identifier type
   * @returns SendOtpViewModel Success
   * @throws ApiError
   */
  public static async sendOtp(
    {
      body,
    }: {
      body?: SendOtpRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SendOtpViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<SendOtpViewModel>({
      method: 'POST',
      url: `/v2/auth/otp`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Check Otp
   * Check otp code
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async checkOtp(
    {
      body,
    }: {
      body?: CheckOtpRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/auth/otp/check`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Check SSO User
   * Gets sso user statis
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async checkSsoUser(
    {
      email,
    }: {
      email: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'GET',
      url: `/v2/auth/validate`,
      query: {
        ...(email ? { email: email } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Activities
   * Gets the auth activities
   * @returns GetActivityViewModel Success
   * @throws ApiError
   */
  public static async getActivities(
    {
      authorization,
    }: {
      /**
       * Authorization
       */
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<GetActivityViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<GetActivityViewModel>>({
      method: 'GET',
      url: `/v2/auth/activities`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}

declare module '@setur/api/v2' {
  interface Services {
    AuthenticationService: typeof AuthenticationService;
  }
}
